<template>
    <div
      v-if="visible"
      ref="el"
      class="bubble"
      :class="{
        'bubble--hidden': !screen.visible,
        'bubble--pipe': isPipe,
        'bubble--polder': isPolder,
      }"
      :style="bubbleStyle"
      @click.stop
    >
      <header class="bubble__head">
        <div class="bubble__titles">
          <p v-if="fields.图层" class="bubble__layer">{{ fields.图层 }}</p>
          <h3>{{ headTitle }}</h3>
        </div>
        <div class="bubble__actions">
          <button
            v-if="showHistoryBtn"
            type="button"
            class="bubble__stat"
            title="历史曲线"
            @click="openHistory"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                fill="currentColor"
                d="M3.5 18.5h17v1.5H3.5zm1.2-2.2 3.1-4.2 2.8 2.1 4.4-5.8 1.2.9-5.4 7.1-2.8-2.1-2.2 3zM19 4.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z"
              />
            </svg>
          </button>
          <button type="button" class="bubble__close" @click="emit('close')">
            ×
          </button>
        </div>
      </header>

      <div class="bubble__body" :class="{ 'bubble__body--polder': isPolder }">
        <!-- 圩区：左右布局（图 + 详情） -->
        <template v-if="isPolder">
          <div class="polder-layout">
            <button
              type="button"
              class="polder-layout__media"
              title="点击预览大图"
              @click="previewOpen = true"
            >
              <img :src="polderImage" alt="圩区实景" />
              <span class="polder-layout__zoom">预览</span>
            </button>
            <div class="polder-layout__info">
              <dl v-if="polderRows.length" class="bubble__list">
                <div v-for="row in polderRows" :key="row.key" class="bubble__row">
                  <dt>{{ row.key }}</dt>
                  <dd>{{ row.value }}</dd>
                </div>
              </dl>
              <p v-else class="bubble__empty">暂无更多属性</p>
            </div>
          </div>
        </template>

        <!-- 供水管段：图例式布局 -->
        <template v-else-if="isPipe">
        <ul class="pipe-meta">
          <li>
            <i class="pipe-meta__dot" />
            <span class="pipe-meta__k">管段编号：</span>
            <span class="pipe-meta__v">{{ pipe.code }}</span>
          </li>
          <li>
            <i class="pipe-meta__dot" />
            <span class="pipe-meta__k">管段位置：</span>
            <span class="pipe-meta__v">{{ pipe.location }}</span>
          </li>
          <li>
            <i class="pipe-meta__dot" />
            <span class="pipe-meta__k">管点信息</span>
          </li>
        </ul>

        <table class="pipe-table">
          <thead>
            <tr>
              <th></th>
              <th>管点编号</th>
              <th>经度</th>
              <th>纬度</th>
              <th>管底高程(m)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>起点</td>
              <td>{{ pipe.start.id }}</td>
              <td>{{ pipe.start.lng }}</td>
              <td>{{ pipe.start.lat }}</td>
              <td>{{ pipe.start.elev }}</td>
            </tr>
            <tr>
              <td>终点</td>
              <td>{{ pipe.end.id }}</td>
              <td>{{ pipe.end.lng }}</td>
              <td>{{ pipe.end.lat }}</td>
              <td>{{ pipe.end.elev }}</td>
            </tr>
          </tbody>
        </table>

        <div class="pipe-grid">
          <div class="pipe-grid__item">
            <span class="pipe-grid__k">管长：</span>
            <span class="pipe-grid__v">{{ pipe.length }}</span>
          </div>
          <div class="pipe-grid__item">
            <span class="pipe-grid__k">管径：</span>
            <span class="pipe-grid__v">{{ pipe.diam }}</span>
          </div>
          <div class="pipe-grid__item">
            <span class="pipe-grid__k">管材：</span>
            <span class="pipe-grid__v">{{ pipe.material }}</span>
          </div>
          <div class="pipe-grid__item">
            <span class="pipe-grid__k">管龄：</span>
            <span class="pipe-grid__v">{{ pipe.age }}</span>
          </div>
          <div class="pipe-grid__item">
            <span class="pipe-grid__k">接口形式：</span>
            <span class="pipe-grid__v">{{ pipe.joint }}</span>
          </div>
          <div class="pipe-grid__item">
            <span class="pipe-grid__k">探测日期：</span>
            <span class="pipe-grid__v">{{ pipe.surveyDate }}</span>
          </div>
        </div>
      </template>

      <!-- 其它要素：原属性列表 -->
      <template v-else>
        <dl v-if="rows.length" class="bubble__list">
          <div v-for="row in rows" :key="row.key" class="bubble__row">
            <dt>{{ row.key }}</dt>
            <dd>{{ row.value }}</dd>
          </div>
        </dl>
        <p v-else class="bubble__empty">暂无更多属性</p>
      </template>
    </div>

    <i class="bubble__arrow" aria-hidden="true" />
  </div>

  <!-- 圩区图片大图预览 -->
  <Teleport to="body">
    <div
      v-if="previewOpen && polderImage"
      class="polder-preview"
      @click.self="previewOpen = false"
    >
      <button
        type="button"
        class="polder-preview__close"
        title="关闭"
        @click="previewOpen = false"
      >
        ×
      </button>
      <img
        class="polder-preview__img"
        :src="polderImage"
        alt="圩区实景大图"
        @click.stop
      />
    </div>
  </Teleport>

  <HistoryChartModal
    :open="historyOpen"
    :fields="fields"
    @close="historyOpen = false"
  />
</template>

<script setup>
import * as Cesium from "cesium";
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useMapStore } from "@/composables/useMapStore";
import { canShowHistory } from "@/utils/mockHistory";
import HistoryChartModal from "@/components/HistoryChartModal.vue";
import wq1 from "@/assets/image/wq1.jpeg";
import wq2 from "@/assets/image/wq2.jpeg";
import wq3 from "@/assets/image/wq3.jpeg";
import wq4 from "@/assets/image/wq4.jpeg";
import wq5 from "@/assets/image/wq5.jpeg";
import wq6 from "@/assets/image/wq6.jpeg";
import wq7 from "@/assets/image/wq7.jpeg";

const POLDER_IMAGES = [wq1, wq2, wq3, wq4, wq5, wq6, wq7];

const props = defineProps({
  picked: { type: Object, default: null },
});

const emit = defineEmits(["close"]);
const map = useMapStore();

const el = ref(null);
const historyOpen = ref(false);
const previewOpen = ref(false);
const polderImage = ref(POLDER_IMAGES[0]);
const screen = reactive({ x: 0, y: 0, visible: false });

const HIDDEN = new Set([
  "名称",
  "图层",
  "name",
  "fillColor",
  "borderColor",
  "centerLng",
  "centerLat",
]);

const POLDER_FIELD_ORDER = [
  "编码",
  "位置",
  "行政区",
  "分类",
  "面积",
  "运行状态",
  "工程状态",
  "涉及村居",
  "堤长",
  "堤顶高程",
  "堤顶宽",
  "设计流量",
  "排涝流量",
  "排涝动力",
  "泵站数量",
  "水闸数量",
  "运管单位",
  "开工日期",
  "完工日期",
  "更新日期",
  "描述",
];

/** 通用属性字段中文名 */
const FIELD_LABEL_ZH = {
  pno: "点号",
  sno: "图上点号",
  center: "中心坐标",
  featureType: "类型",
  address: "地址",
  gid: "GID",
  objectid: "对象ID",
  id: "编号",
  type: "类型",
  appearance: "井盖形式",
  size: "口径(mm)",
  depth: "埋深(m)",
  gelev: "地面高程(m)",
  pelev: "井底高程(m)",
  embed: "埋设方式",
  bz: "备注",
  x: "经度",
  y: "纬度",
};

/** 管材种编码（常见供水 GIS） */
const GCZ_MAP = {
  0: "球墨铸铁",
  1: "钢管",
  2: "PE",
  3: "PVC",
  4: "混凝土",
  5: "玻璃钢",
  6: "不锈钢",
  7: "其他",
};

const visible = computed(() => !!props.picked?.fields);
const fields = computed(() => props.picked?.fields || {});
const showHistoryBtn = computed(() => canShowHistory(fields.value));

const isSupplyPipe = computed(() => {
  const f = fields.value;
  return (
    (f.qddh != null || f.zddh != null) &&
    (f.gj != null || f.gcd != null || f.qdjd != null)
  );
});

/** 排水真管：fno/tno + diameter */
const isDrainPipe = computed(() => {
  const f = fields.value;
  return (f.fno != null || f.tno != null) && f.diameter != null;
});

const isPipe = computed(() => isSupplyPipe.value || isDrainPipe.value);

/** 总览 / 水雨情：圩区详情左右布局 + 随机实景图 */
const isPolder = computed(() => {
  if (props.picked?.kind !== "polder") return false;
  const page = map.activePage;
  return page === "home" || page === "hydro";
});

const headTitle = computed(() => {
  if (isPolder.value) {
    return fields.value.名称 || fields.value.编码 || "圩区详情";
  }
  if (isSupplyPipe.value) {
    return fields.value.code || fields.value.名称 || "管段详情";
  }
  if (isDrainPipe.value) {
    const f = fields.value;
    const code =
      f.fno && f.tno ? `${f.tno}${f.fno}` : f.fno || f.tno || f.pno;
    return code || fields.value.名称 || "管段详情";
  }
  // 井/栓等点要素：优先点号，避免标题只剩中文类型名
  return (
    fields.value.pno ||
    fields.value.address ||
    fields.value.名称 ||
    "要素详情"
  );
});

const polderRows = computed(() => {
  const f = fields.value;
  const rows = [];
  for (const key of POLDER_FIELD_ORDER) {
    const v = f[key];
    if (v == null || String(v).trim() === "") continue;
    rows.push({ key, value: String(v) });
  }
  // 兜底：其它未隐藏字段
  for (const [k, v] of Object.entries(f)) {
    if (HIDDEN.has(k) || POLDER_FIELD_ORDER.includes(k)) continue;
    if (v == null || String(v).trim() === "") continue;
    rows.push({ key: FIELD_LABEL_ZH[k] || k, value: String(v) });
  }
  return rows.slice(0, 8);
});

function pickPolderImage() {
  const i = Math.floor(Math.random() * POLDER_IMAGES.length);
  return POLDER_IMAGES[i];
}

function dash(v) {
  if (v == null) return "-";
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined" || s === "None") return "-";
  return s;
}

function fmtNum(v, digits = 4) {
  const n = Number(v);
  if (!Number.isFinite(n)) return dash(v);
  return n.toFixed(digits);
}

function fmtElev(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return dash(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function materialOf(f) {
  const raw = f.material ?? f.gcz ?? f.管材;
  if (raw == null || String(raw).trim() === "") return "-";
  const s = String(raw).trim();
  if (/[\u4e00-\u9fa5a-zA-Z]/.test(s) && !/^\d+$/.test(s)) return s;
  return GCZ_MAP[s] || GCZ_MAP[Number(s)] || s;
}

const pipe = computed(() => {
  const f = fields.value;

  // 排水真管字段
  if (isDrainPipe.value && !isSupplyPipe.value) {
    const len = Number(f.length ?? f.shapeLeng);
    const diam = Number(f.diameter);
    const code =
      f.fno && f.tno ? `${f.tno}${f.fno}` : f.fno || f.tno || f.pno;
    return {
      code: dash(code),
      location: dash(f.road || f.location || f.ssqx),
      start: {
        id: dash(f.fno),
        lng: "-",
        lat: "-",
        elev: fmtElev(f.inElev ?? f.fdepth ?? f.elevationS),
      },
      end: {
        id: dash(f.tno),
        lng: "-",
        lat: "-",
        elev: fmtElev(f.outElev ?? f.tdepth ?? f.depthE),
      },
      length: Number.isFinite(len) ? `${Number(len.toFixed(2))}m` : dash(f.length),
      diam: Number.isFinite(diam) ? `${diam}mm` : dash(f.diameter),
      material: materialOf(f),
      age: "-",
      joint: dash(f.embed || f.protect),
      surveyDate: dash(f.surveyDat || f.cdate),
    };
  }

  // 供水管 / 测绘管段
  const len = Number(f.gcd);
  const diam = Number(f.gj);
  const age = f.gl;
  return {
    code: dash(f.code),
    location: dash(f.dz || f.地址),
    start: {
      id: dash(f.qddh),
      lng: fmtNum(f.qdjd, 4),
      lat: fmtNum(f.qdwd, 4),
      elev: fmtElev(f.qdgc),
    },
    end: {
      id: dash(f.zddh),
      lng: fmtNum(f.zdjd, 4),
      lat: fmtNum(f.zdwd, 4),
      elev: fmtElev(f.zdgc),
    },
    length: Number.isFinite(len) ? `${len}m` : dash(f.gcd),
    diam: Number.isFinite(diam) ? `${diam}mm` : dash(f.gj),
    material: materialOf(f),
    age: age == null || String(age).trim() === "" ? "-" : `${dash(age)}年`,
    joint: dash(f.jkxs),
    surveyDate: dash(f.tcrq),
  };
});

const rows = computed(() => {
  const f = fields.value;
  return Object.entries(f)
    .filter(([k, v]) => {
      if (HIDDEN.has(k) || v == null || String(v).trim() === "") return false;
      // 类型与图层名重复时不再展示
      if (
        (k === "featureType" || k === "type") &&
        (String(v) === String(f.图层) || String(v) === String(f.名称))
      ) {
        return false;
      }
      return true;
    })
    .slice(0, 8)
    .map(([key, value]) => ({
      key: FIELD_LABEL_ZH[key] || key,
      value: String(value),
    }));
});

const bubbleStyle = computed(() => ({
  left: `${screen.x}px`,
  top: `${screen.y}px`,
}));

let removePostRender = null;

function openHistory() {
  historyOpen.value = true;
}

function updateScreenPos() {
  const v = map.viewer;
  const cartesian = props.picked?.cartesian;
  if (!v || v.isDestroyed?.() || !cartesian) {
    screen.visible = false;
    return;
  }
  const win = v.scene.cartesianToCanvasCoordinates(cartesian);
  if (!Cesium.defined(win)) {
    screen.visible = false;
    return;
  }

  const canvas = v.scene.canvas;
  const rect = canvas.getBoundingClientRect();
  let x = rect.left + win.x;
  let y = rect.top + win.y - 22;

  const pad = 12;
  const w =
    el.value?.offsetWidth ||
    (isPolder.value ? 520 : isPipe.value ? 480 : 260);
  const h = el.value?.offsetHeight || 160;
  const minX = pad + w / 2;
  const maxX = window.innerWidth - pad - w / 2;
  const minY = pad + h + 18;
  x = Math.min(maxX, Math.max(minX, x));
  y = Math.min(window.innerHeight - pad, Math.max(minY, y));

  screen.x = x;
  screen.y = y;
  screen.visible =
    win.x > -40 &&
    win.y > -40 &&
    win.x < canvas.clientWidth + 40 &&
    win.y < canvas.clientHeight + 40;
}

function bindPostRender() {
  unbindPostRender();
  const v = map.viewer;
  if (!v || v.isDestroyed?.()) return;
  const cb = () => updateScreenPos();
  v.scene.postRender.addEventListener(cb);
  removePostRender = () => {
    try {
      if (!v.isDestroyed?.()) {
        v.scene.postRender.removeEventListener(cb);
      }
    } catch (_) {
      /* ignore */
    }
  };
}

function unbindPostRender() {
  removePostRender?.();
  removePostRender = null;
}

function onKey(e) {
  if (e.key !== "Escape") return;
  if (previewOpen.value) {
    previewOpen.value = false;
    return;
  }
  if (!historyOpen.value) emit("close");
}

watch(
  () => props.picked,
  (val) => {
    historyOpen.value = false;
    previewOpen.value = false;
    if (val) {
      if (val.kind === "polder") {
        polderImage.value = pickPolderImage();
      }
      bindPostRender();
      requestAnimationFrame(updateScreenPos);
    } else {
      unbindPostRender();
    }
  },
  { immediate: true }
);

onMounted(() => window.addEventListener("keydown", onKey));
onUnmounted(() => {
  window.removeEventListener("keydown", onKey);
  unbindPostRender();
});
</script>

<style scoped>
.bubble {
  position: fixed;
  z-index: 80;
  width: 260px;
  max-height: 280px;
  transform: translate(-50%, calc(-100% - 14px));
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    165deg,
    rgba(6, 22, 38, 0.97) 0%,
    rgba(4, 14, 26, 0.96) 100%
  );
  border: 1px solid rgba(61, 231, 255, 0.4);
  box-shadow:
    0 8px 28px rgba(0, 0, 0, 0.5),
    0 0 18px rgba(0, 180, 255, 0.12);
  pointer-events: auto;
}

.bubble--pipe {
  width: 500px;
  max-height: 420px;
}

.bubble--polder {
  width: 520px;
  max-height: 420px;
}

.bubble--hidden {
  visibility: hidden;
  pointer-events: none;
}

.bubble__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 10px 8px;
  border-bottom: 1px solid rgba(61, 231, 255, 0.16);
  background: rgba(61, 231, 255, 0.06);
}

.bubble__layer {
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--cyan, #3de7ff);
  margin-bottom: 2px;
}

.bubble__titles h3 {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  line-height: 1.35;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bubble--pipe .bubble__titles h3 {
  max-width: 400px;
}

.bubble--polder .bubble__titles h3 {
  max-width: 360px;
}

.bubble__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.bubble__stat {
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(61, 231, 255, 0.35);
  background: rgba(61, 231, 255, 0.1);
  color: #3de7ff;
  cursor: pointer;
  padding: 0;
}

.bubble__stat:hover {
  background: rgba(61, 231, 255, 0.22);
  border-color: rgba(61, 231, 255, 0.65);
  box-shadow: 0 0 10px rgba(61, 231, 255, 0.35);
}

.bubble__close {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: rgba(216, 246, 255, 0.7);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  font-family: inherit;
}

.bubble__close:hover {
  color: #fff;
  border-color: rgba(61, 231, 255, 0.4);
}

.bubble__body {
  padding: 8px 10px 12px;
  overflow-y: auto;
  scrollbar-width: thin;
  max-height: 200px;
}

.bubble--pipe .bubble__body {
  max-height: 360px;
  padding: 12px 14px 14px;
}

.bubble__body--polder {
  max-height: 340px;
  padding: 10px 12px 12px;
  overflow: hidden;
}

.polder-layout {
  display: grid;
  grid-template-columns: 168px 1fr;
  gap: 12px;
  align-items: stretch;
  min-height: 180px;
  height: 100%;
  max-height: 320px;
}

.polder-layout__media {
  position: relative;
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(61, 231, 255, 0.22);
  background: rgba(0, 0, 0, 0.25);
  overflow: hidden;
  min-height: 180px;
  cursor: zoom-in;
  font: inherit;
  color: inherit;
}

.polder-layout__media:hover {
  border-color: rgba(61, 231, 255, 0.55);
}

.polder-layout__media:hover .polder-layout__zoom {
  opacity: 1;
}

.polder-layout__media img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 180px;
  max-height: 320px;
  object-fit: cover;
  pointer-events: none;
}

.polder-layout__zoom {
  position: absolute;
  right: 6px;
  bottom: 6px;
  padding: 2px 8px;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: #e8f7ff;
  background: rgba(2, 12, 22, 0.72);
  border: 1px solid rgba(61, 231, 255, 0.35);
  opacity: 0.75;
  transition: opacity 0.15s;
  pointer-events: none;
}

.polder-layout__info {
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(61, 231, 255, 0.4) transparent;
}

.polder-layout__info .bubble__row {
  grid-template-columns: 72px 1fr;
}

.polder-layout__info .bubble__row dd {
  white-space: normal;
}

@media (max-width: 560px) {
  .bubble--polder {
    width: min(520px, calc(100vw - 24px));
  }

  .polder-layout {
    grid-template-columns: 1fr;
    max-height: none;
  }

  .polder-layout__media img {
    max-height: 140px;
    min-height: 120px;
  }
}

.bubble__list {
  display: flex;
  flex-direction: column;
}

.bubble__row {
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 6px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 12px;
}

.bubble__row:last-child {
  border-bottom: none;
}

.bubble__row dt {
  color: rgba(216, 246, 255, 0.45);
}

.bubble__row dd {
  color: #e8f7ff;
  word-break: break-word;
  line-height: 1.4;
}

.bubble__empty {
  font-size: 12px;
  color: rgba(216, 246, 255, 0.4);
  padding: 6px 0;
}

.bubble__arrow {
  position: absolute;
  left: 50%;
  bottom: -8px;
  width: 14px;
  height: 14px;
  margin-left: -7px;
  background: rgba(5, 18, 30, 0.97);
  border-right: 1px solid rgba(61, 231, 255, 0.4);
  border-bottom: 1px solid rgba(61, 231, 255, 0.4);
  transform: rotate(45deg);
  box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.25);
}

/* —— 管段专用布局 —— */
.pipe-meta {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
}

.pipe-meta li {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
  line-height: 1.4;
  color: rgba(232, 247, 255, 0.92);
}

.pipe-meta__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #3de7ff;
  flex-shrink: 0;
  margin-top: 5px;
  box-shadow: 0 0 6px rgba(61, 231, 255, 0.55);
}

.pipe-meta__k {
  color: rgba(216, 246, 255, 0.55);
  flex-shrink: 0;
}

.pipe-meta__v {
  color: #fff;
  word-break: break-all;
}

.pipe-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 14px;
}

.pipe-table th,
.pipe-table td {
  padding: 8px 6px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  white-space: nowrap;
}

.pipe-table th {
  color: rgba(216, 246, 255, 0.5);
  font-weight: 500;
  background: rgba(255, 255, 255, 0.04);
}

.pipe-table td {
  color: #e8f7ff;
}

.pipe-table td:first-child,
.pipe-table th:first-child {
  color: rgba(216, 246, 255, 0.55);
  width: 36px;
  padding-left: 4px;
}

.pipe-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 20px;
  font-size: 13px;
  line-height: 1.45;
}

.pipe-grid__k {
  color: rgba(216, 246, 255, 0.55);
}

.pipe-grid__v {
  color: #fff;
}

@media (max-width: 560px) {
  .bubble--pipe {
    width: min(500px, calc(100vw - 24px));
  }

  .pipe-table {
    font-size: 11px;
  }

  .pipe-table th,
  .pipe-table td {
    padding: 6px 4px;
  }
}
</style>

<style>
/* Teleport 到 body，非 scoped */
.polder-preview {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(2, 8, 16, 0.82);
  backdrop-filter: blur(6px);
}

.polder-preview__img {
  max-width: min(920px, 92vw);
  max-height: 88vh;
  object-fit: contain;
  border: 1px solid rgba(61, 231, 255, 0.35);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.55);
}

.polder-preview__close {
  position: absolute;
  top: 18px;
  right: 22px;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(61, 231, 255, 0.4);
  background: rgba(6, 22, 38, 0.85);
  color: #e8f7ff;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  font-family: inherit;
}

.polder-preview__close:hover {
  border-color: #3de7ff;
  color: #fff;
}
</style>
